'use client';

import { useMemo } from 'react';
import type { UserProfile } from '@/entities/user-profile';
import { SplitWizardProvider } from '../model/split-wizard-context';
import { SplitWizardSubmitProvider } from '../model/split-wizard-submit-context';
import type { SplitWizardData } from '../model/split-wizard-state';
import { getOnboardingSteps } from '../model/onboarding-steps';
import { useCompleteOnboarding } from '@/features/auth-onboarding';
import { OnboardingStepSwitch } from './onboarding-step-switch';


type Props = {
    profile: UserProfile;
};


const buildInitialData = (profile: UserProfile): SplitWizardData => ({
    email  : { email: profile.email },
    profile: {
        first_name: profile.first_name,
        last_name : profile.last_name,
        job_title : profile.job_title,
        phone     : profile.phone,
    },
    company: {
        company_name: profile.company_name,
        company_vat : profile.company_vat,
        address     : profile.address,
        city        : profile.city,
        postal_code : profile.postal_code,
        country     : profile.country,
    },
    plan: {
        plan_id       : '',
        billing_period: profile.billing_period,
    },
});


export const OnboardingWizard = ({ profile }: Props) => {
    const initialData                = useMemo(() => buildInitialData(profile), [ profile ]);
    const companyRequired            = Boolean(profile.onboarding_required);
    const { initialStep, stepOrder } = useMemo(() => getOnboardingSteps(companyRequired), [ companyRequired ]);

    const submitValue = useCompleteOnboarding({ includeCompany: companyRequired });

    return (
        <SplitWizardProvider
            initialStep={ initialStep }
            initialData={ initialData }
            stepOrder={ stepOrder }
        >
            <SplitWizardSubmitProvider value={ submitValue }>
                <div data-template="onboarding-wizard">
                    <OnboardingStepSwitch/>
                </div>
            </SplitWizardSubmitProvider>
        </SplitWizardProvider>
    );
};
